import { createContext, ReactNode, useCallback, useEffect, useState } from 'react'
import { Help_And_Instruction_Link, Query_Root } from 'src/@types/graphql'
import { useUserLocale } from 'src/user/UserContext'
import { Client, gql, useClient } from 'urql'
import { SupportedLocale } from 'src/@types'
import { useLocalStorage } from 'src/shared/utils/hooks/local-storage-hooks'
import { STORAGE_KEY } from 'src/shared/utils/StorageUtils'

interface HelpAndInstructionLinkModel {
  id: number
  key: string
  label: string
  link: string
}

export type HelpAndInstructionLinksContextShape = {
  getLinks: (key: string) => HelpAndInstructionLinkModel[]
  syncHelpInstructionLinks: () => void
}

/**
 * Initial Context value, an empty array.
 */
const empty: HelpAndInstructionLinksContextShape = {
  getLinks: () => [],
  syncHelpInstructionLinks: () => {},
}

/**
 * A React Context which holds the help and instructions links map.
 */
export const HelpAndInstructionLinksContext = createContext<HelpAndInstructionLinksContextShape>(empty)
HelpAndInstructionLinksContext.displayName = 'HelpAndInstructionLinksContext'

const fetchHelpAndInstructionsLinksQuery = gql`
  query fetchHelpAndInstructionsLinks($language: String!) {
    help_and_instruction_link(order_by: { id: asc }) {
      id
      key
      labels(path: $language)
      links(path: $language)
    }
  }
`

const fetchHelpInstructionLinks = (urqlClient: Client, locale: SupportedLocale) => async () => {
  const { data, error } = await urqlClient
    .query<{ help_and_instruction_link: Query_Root['help_and_instruction_link'] }, { language: string }>(
      fetchHelpAndInstructionsLinksQuery,
      { language: locale },
    )
    .toPromise()

  if (error || !data?.help_and_instruction_link) {
    throw new Error('Resource not found')
  }

  return { fetchedHelpInstructionLinks: data.help_and_instruction_link }
}

export const HelpAndInstructionLinksContextProvider = ({ children }: { children: ReactNode }) => {
  const locale = useUserLocale()
  const urqlClient = useClient()

  const [helpInstructionsShouldSync, setHelpInstructionsShouldSync] = useLocalStorage<boolean>(
    STORAGE_KEY._HELP_INSTRUCTION_LINKS_UPDATED_STORAGE,
    true,
  )
  const [helpInstructionLinks, setHelpInstructionLinks] = useState<Help_And_Instruction_Link[]>([])

  const initHelpInstructionLinks = useCallback(() => {
    fetchHelpInstructionLinks(urqlClient, locale)()
      .then(({ fetchedHelpInstructionLinks }) => {
        setHelpInstructionLinks(fetchedHelpInstructionLinks)
        setHelpInstructionsShouldSync(false)
      })
      .catch((_) => {
        /* empty */
      })
  }, [urqlClient, locale, setHelpInstructionsShouldSync])

  useEffect(() => {
    setHelpInstructionsShouldSync(true)
  }, [locale, setHelpInstructionsShouldSync])

  useEffect(() => {
    if (helpInstructionsShouldSync) {
      initHelpInstructionLinks()
    }
  }, [initHelpInstructionLinks, helpInstructionsShouldSync])

  const getSafeLink = (link: string) => {
    if (link.startsWith('http')) {
      return link
    }

    return `https://${link}`
  }

  const mapLinksToHelpAndInstructionLinksModel = (
    links: Help_And_Instruction_Link[],
  ): HelpAndInstructionLinkModel[] => {
    return links.map(({ id, key, labels, links }) => ({
      id: id,
      key: key,
      label: labels,
      link: getSafeLink(links),
    }))
  }

  const getLinks = (key: string) => {
    return mapLinksToHelpAndInstructionLinksModel(helpInstructionLinks.filter((link) => link.key === key))
  }

  const syncHelpInstructionLinks = () => setHelpInstructionsShouldSync(true)

  return (
    <HelpAndInstructionLinksContext.Provider value={{ getLinks, syncHelpInstructionLinks }}>
      {children}
    </HelpAndInstructionLinksContext.Provider>
  )
}
